import { useQueries } from '@tanstack/react-query';
import { endpoints, getIDFromUrl } from '@rossum/api-client';
import {
  DatapointData,
  MultivalueData,
  SectionData,
  TupleData,
} from '@rossum/api-client/annotations';
import { SchemaSection } from '@rossum/api-client/schemas';
import { api } from '../../../../lib/apiClient';
import { FORMULA_PREVIEW_KEY } from '../../keys';
import {
  ContentForPreview,
  makeArtificialContent,
} from './makeArtificialContent';

type ContentItem = SectionData | MultivalueData | TupleData | DatapointData;

const mapDatapoints = <T extends ContentItem>(
  item: T,
  fn: (dp: DatapointData) => DatapointData
): T => {
  if (item.category === 'datapoint') {
    return fn(item) as T;
  }

  if (item.category === 'tuple') {
    return {
      ...item,
      children: item.children.map(child => (child ? fn(child) : child)),
    };
  }

  if (item.category === 'multivalue') {
    return {
      ...item,
      children: item.children?.map(child => mapDatapoints(child, fn)),
    };
  }

  if (item.category === 'section') {
    return {
      ...item,
      children: item.children?.map(child => mapDatapoints(child, fn)),
    };
  }

  return item;
};

const enableRecalculationForSchemaId = (
  content: SectionData[],
  schemaId: string
): SectionData[] =>
  content.map(section =>
    mapDatapoints(section, dp =>
      dp.schemaId === schemaId ? { ...dp, noRecalculation: false } : dp
    )
  );

type UseCalculatePreview = {
  content: ContentForPreview[];
  schemaContent: SchemaSection[];
  currentFormulaId: string;
  disabled?: boolean;
};

export const useCalculatePreview = ({
  content,
  schemaContent,
  currentFormulaId,
  disabled,
}: UseCalculatePreview) => {
  const queries = content.map(item => {
    const queryKey = [
      FORMULA_PREVIEW_KEY,
      'annotation-content',
      item,
      schemaContent,
    ] as const;

    return {
      queryKey,
      queryFn: async () => {
        const annotationContent =
          item.type === 'from_annotation'
            ? await api
                .request(
                  endpoints.annotations.getContent(
                    getIDFromUrl(item.annotationUrl)
                  )
                )
                .then(({ results }) =>
                  enableRecalculationForSchemaId(results, currentFormulaId)
                )
            : makeArtificialContent(item.dependencies, schemaContent);

        return api
          .request(
            endpoints.internal.schemas.evaluateFormulas({
              annotationContent,
              schemaContent,
              annotation:
                item.type === 'from_annotation'
                  ? item.annotationUrl
                  : undefined,
              // For the preview row we mock the annotation, so it carries no
              // queue context; pass the current queue so formulas can resolve
              // queue-dependent values.
              queue: item.type === 'from_annotation' ? undefined : item.queue,
            })
          )
          .then(response => ({
            // adding the annotation id to the response for easier matching
            ...response,
            content: item,
          }));
      },
      enabled: schemaContent.length > 0 && !disabled,
    };
  });

  return useQueries({ queries });
};
